/**
 * Build Your Floating Locket (snippets/floating-locket-builder.liquid).
 *
 * Quick-start sets -> 1. choose a locket -> 2. fill it with charms -> 3. review -> cart, with a live preview that
 * redraws what the product photos' compositor (floating_gif.py) draws: each charm at its true size in mm, settled
 * by gravity inside the glass window, then multiplied by the glass's shading, screened by its bright facet edges,
 * veiled by the glass colour and given a faint contact shadow (maps in fl-preview-data.liquid).
 *
 * Cart model = the charm-set quick-start bundles (ab-test-charm-v4.js): one locket line (_charm_bundle_parent,
 * visible "Charm N" titles for the packer) + one line per charm (_charm_bundle_child). ajax-cart.js shows them as one
 * row, the BOGO preview counts every unit, and removing the locket removes its charms.
 *
 * Accessibility (CLAUDE.md, WCAG 2.2 AA): native buttons/radios, native <dialog> (focus moves in, Escape closes,
 * focus returns to the opener), aria-pressed filters, a polite status region for every add/remove, the canvas is a
 * role=img whose label lists the charms, reduced motion respected.
 *
 * The builder-agnostic parts (status region, dialogs, photo upload, variant picker, add to cart, navigation) live in
 * assets/wf-builder-kit.js, shared with the Mini Custom Charm builder.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fl-root]');
  var K = window.WFBuilderKit;
  if (!root || root.__fl || !K) return;
  root.__fl = true;

  var DATA, PV;
  try {
    DATA = JSON.parse(root.querySelector('[data-fl-data]').textContent);
    PV = JSON.parse(root.querySelector('[data-fl-preview]').textContent);
  } catch (e) {
    console.error('[FloatingLocket] bad data', e);
    return;
  }
  var CFG = DATA.config;
  var REDUCED = K.REDUCED;
  var STORE_KEY = 'wf_floating_locket_v1';

  /* ---------------------------------------------------------------------------------------------- utils */
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  var esc = K.esc, money = K.money, uid = K.uid, plural = K.plural;
  function shortName(title) {
    return String(title || '').replace(/^\s*Floating\s+/i, '').replace(/\s+Charm\s*$/i, '').trim();
  }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  var dialogs = {
    set: $('[data-fl-dialog="set"]'), picker: $('[data-fl-dialog="picker"]'),
    photo: $('[data-fl-dialog="photo"]'), zoom: $('[data-fl-dialog="zoom"]')
  };
  var UI = K.ui(root, { dialogs: dialogs });
  var announce = UI.announce, openDialog = UI.openDialog, closeDialog = UI.closeDialog;

  /* -------------------------------------------------------------------------------------------- catalog */
  var CHARMS = (DATA.charms || []).filter(function (p) { return p && p.variants && p.variants.length; });
  var BASES = [];
  (CFG.bases || []).forEach(function (b, i) {
    var p = (DATA.bases || [])[i];
    if (p && p.variants && p.variants.length && PV.bases[b.key]) {
      BASES.push({ key: b.key, label: b.label, short: b.short || b.label, blurb: b.blurb, min: b.min, max: b.max, space: b.space,
                   product: p, geo: PV.bases[b.key] });
    }
  });
  if (!BASES.length || !CHARMS.length) {
    console.warn('[FloatingLocket] no lockets or charms published');
    root.classList.add('fl--empty');
    root.insertAdjacentHTML('afterbegin', '<p class="fl-lede">Our floating lockets are coming soon. Check back shortly to build yours.</p>');
    return;
  }
  function baseByKey(k) { for (var i = 0; i < BASES.length; i++) if (BASES[i].key === k) return BASES[i]; return null; }

  var SKU = {};
  CHARMS.forEach(function (p) {
    K.prepProduct(p, shortName);
    p.isPhoto = /photo/i.test(p.handle) || p.variants.some(function (v) { return PV.charms[v.sku] && PV.charms[v.sku].photo; });
    p.variants.forEach(function (v) { if (v.sku) SKU[v.sku] = v; });
  });
  var ORDER = CFG.order || [];
  function orderIndex(p) {
    for (var i = 0; i < ORDER.length; i++) if (p.handle.indexOf(ORDER[i]) > -1) return i;
    return ORDER.length;
  }
  CHARMS.sort(function (a, b) { return orderIndex(a) - orderIndex(b); });

  var MONTHS = K.MONTHS, BS_MONTH = K.BS_MONTH;
  var variantValue = K.variantValue, itemName = K.itemName, variantLabel = K.variantLabel, isStone = K.isStone;
  /** the stone itself, trimmed (the preview's cutout), else the variant's product photo */
  function stoneThumb(v) { var c = PV.charms[v.sku]; return c && c.img ? c.img : (v.img || v.product.img); }
  function cartTitle(v) {
    var p = v.product;
    return p.title + (p.multi ? ' - ' + variantValue(v) : '');
  }

  /* ---------------------------------------------------------------------------------------------- state */
  var state = { baseKey: null, baseVariantId: null, items: [], cat: 'all' };

  function currentBase() { return baseByKey(state.baseKey); }
  function baseVariant(base, preferId) {
    if (!base) return null;
    var vs = base.product.variants;
    if (preferId) for (var i = 0; i < vs.length; i++) if (vs[i].id === preferId && vs[i].available) return vs[i];
    if (vs.length === 1) return vs[0].available ? vs[0] : null;
    return null; // several sizes: the shopper picks one
  }
  /*
   * Size-aware capacity (owner, 2026-10-14: "RING: 3-6 CHARMS MAX / NECKLACE: 5-8 CHARMS MAX DEPENDING ON CHARMS SIZE").
   * Every locket always takes its `min` charms and never more than `max`; in between, the charms' footprints (their
   * width x height in mm, from the preview data) must add up to no more than the locket's `space` (mm2, about 80% of
   * its glass window). So the ring takes 6 birthstones or Love Story's 4 charms, but not 4 big ones like Lucky Charms.
   */
  function sizeOf(v) { var c = v && PV.charms[v.sku]; return c ? c.w_mm * c.h_mm : 30; }
  /** How many of these charms, in order, go INSIDE the locket (the rest are extras). */
  function insideCount(base, variants) {
    var n = variants.length, k = Math.min(n, base.min), used = 0;
    for (var i = 0; i < k; i++) used += sizeOf(variants[i]);
    while (k < n && k < base.max && used + sizeOf(variants[k]) <= base.space) { used += sizeOf(variants[k]); k++; }
    return k;
  }
  function fitsAll(base, variants) { return insideCount(base, variants) === variants.length; }
  /** the charms inside the current locket, i.e. the index where extras start */
  function capacity() {
    var b = currentBase();
    return b ? insideCount(b, state.items.map(function (it) { return it.variant; })) : state.items.length;
  }
  var SMALLEST = null;
  function smallestSize() {
    if (SMALLEST === null) {
      SMALLEST = Infinity;
      (DATA.charms || []).forEach(function (p) { (p && p.variants || []).forEach(function (v) { if (v.available) SMALLEST = Math.min(SMALLEST, sizeOf(v)); }); });
      if (SMALLEST === Infinity) SMALLEST = 15;
    }
    return SMALLEST;
  }
  function inStock(b) { return b.product.variants.some(function (v) { return v.available; }); }
  /** v itself when in stock, else the first in-stock variant of the same charm (a set's default letter sold out) */
  function inStockVariant(v) {
    if (!v || v.available) return v;
    return v.product.variants.filter(function (x) { return x.available; })[0] || v;
  }

  function save() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        baseKey: state.baseKey, baseVariantId: state.baseVariantId,
        items: state.items.map(function (it) { return { sku: it.variant.sku, photo: it.photo ? { url: it.photo.url } : null }; })
      }));
    } catch (e) { /* private mode */ }
  }
  function restore() {
    try {
      var s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (!s) return false;
      var sb = s.baseKey && baseByKey(s.baseKey);
      if (sb && inStock(sb)) { state.baseKey = s.baseKey; state.baseVariantId = s.baseVariantId || null; }
      (s.items || []).forEach(function (r) {
        var v = SKU[r.sku];
        if (v && v.available) state.items.push(newItem(v, r.photo && r.photo.url ? photoFromUrl(r.photo.url) : null));
      });
      return true;
    } catch (e) { return false; }
  }

  function newItem(v, photo) { return { uid: uid(), variant: v, photo: photo || null }; }
  var photoFromUrl = K.photoFromUrl;

  /* ------------------------------------------------------------------------------------- preview engine */
  var loadImg = K.loadImg;
  function baseMaps(base) {
    var g = base.geo;
    return Promise.all([loadImg(g.img), loadImg(g.shade), loadImg(g.hi), loadImg(g.glass)]);
  }

  var CELL = PV.cell_mm;
  var MASKS = {};
  function decodeMask(sku) {
    if (MASKS[sku]) return MASKS[sku];
    var info = PV.charms[sku];
    if (!info) return null;
    var cols = info.mask[0], rows = info.mask[1];
    var bin = atob(info.mask[2]);
    var m = new Uint8Array(cols * rows);
    for (var i = 0; i < cols * rows; i++) m[i] = (bin.charCodeAt(i >> 3) >> (7 - (i & 7))) & 1;
    MASKS[sku] = { cols: cols, rows: rows, m: m };
    return MASKS[sku];
  }
  var ROT = {};
  /** The charm's mask rotated by deg (counter-clockwise, like PIL), as a list of set cells around its center. */
  function rotMask(sku, deg, scale) {
    scale = scale || 1;
    var key = sku + '@' + deg + '@' + scale;
    if (ROT[key]) return ROT[key];
    var M = decodeMask(sku);
    var t = deg * Math.PI / 180, c = Math.cos(t), s = Math.sin(t);
    var hw = M.cols / 2, hh = M.rows / 2;
    var W = Math.ceil((Math.abs(M.cols * c) + Math.abs(M.rows * s)) * scale) + 1;
    var H = Math.ceil((Math.abs(M.cols * s) + Math.abs(M.rows * c)) * scale) + 1;
    var cells = [];
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        var dx = (x + 0.5 - W / 2) / scale, dy = (y + 0.5 - H / 2) / scale;
        // inverse of a CCW turn on screen (y down): source = R(+t) * dest
        var sx = dx * c - dy * s + hw, sy = dx * s + dy * c + hh;
        var ix = Math.floor(sx), iy = Math.floor(sy);
        if (ix >= 0 && iy >= 0 && ix < M.cols && iy < M.rows && M.m[iy * M.cols + ix]) cells.push(x, y);
      }
    }
    ROT[key] = { w: W, h: H, cells: cells, area: cells.length / 2 };
    return ROT[key];
  }

  /** The window as a grid of safe cells (inside, at least margin_mm from the edge), in mm from the crop's corner. */
  var GRIDS = {};
  function windowGrid(base) {
    if (GRIDS[base.key]) return GRIDS[base.key];
    var g = base.geo, w = g.window, ppm = g.ppm, mar = g.margin_mm;
    var minx, miny, maxx, maxy, inside;
    if (w.type === 'circle') {
      var cx = w.cx / ppm, cy = w.cy / ppm, r = w.r / ppm;
      minx = cx - r; maxx = cx + r; miny = cy - r; maxy = cy + r;
      inside = function (x, y) { return r - Math.hypot(x - cx, y - cy) >= mar; };
    } else {
      var pts = w.points.map(function (p) { return [p[0] / ppm, p[1] / ppm]; });
      minx = Infinity; miny = Infinity; maxx = -Infinity; maxy = -Infinity;
      pts.forEach(function (p) { minx = Math.min(minx, p[0]); maxx = Math.max(maxx, p[0]); miny = Math.min(miny, p[1]); maxy = Math.max(maxy, p[1]); });
      inside = function (x, y) {
        var inn = false;
        for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
          var xi = pts[i][0], yi = pts[i][1], xj = pts[j][0], yj = pts[j][1];
          if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inn = !inn;
        }
        if (!inn) return false;
        for (var k = 0, l = pts.length - 1; k < pts.length; l = k++) {
          if (segDist(x, y, pts[l][0], pts[l][1], pts[k][0], pts[k][1]) < mar) return false;
        }
        return true;
      };
    }
    var GW = Math.ceil((maxx - minx) / CELL), GH = Math.ceil((maxy - miny) / CELL);
    var safe = new Uint8Array(GW * GH);
    var sumx = 0, n = 0;
    for (var y = 0; y < GH; y++) for (var x = 0; x < GW; x++) {
      if (inside(minx + (x + 0.5) * CELL, miny + (y + 0.5) * CELL)) { safe[y * GW + x] = 1; sumx += x; n++; }
    }
    GRIDS[base.key] = { x0: minx, y0: miny, w: GW, h: GH, safe: safe, cx: n ? sumx / n : GW / 2 };
    return GRIDS[base.key];
  }
  function segDist(px, py, ax, ay, bx, by) {
    var dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
    var t = L ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L)) : 0;
    return Math.hypot(px - ax - t * dx, py - ay - t * dy);
  }

  function hash(n) { var x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); }

  /*
   * Packing (owner, 2026-10-14: "the charms never overlap, they should be touching but not overlap").
   * Gravity: each charm, biggest first, goes to the lowest free spot inside the glass (most central among equals),
   * touching what's already in, at a slight tilt. TILTS are tried first because upright charms read best; a charm that
   * only fits turned may tumble (TUMBLE), as loose charms do in a real locket. If a crowded locket still can't lie flat
   * (the cases are 5-7 mm deep and real charms stack, so the capacity rule allows more than a flat layer holds), every
   * charm in the PREVIEW shrinks by the same small step until they all fit. Nothing is ever drawn overlapping.
   */
  var TILTS = [-6, 6, -12, 12, 0, -20, 20];
  var TUMBLE = [-35, 35, -55, 55, 90, -90, 180, 145, -145];
  // tries in order: upright-ish at true size, then true size with a loose birthstone turned any way (a gem reads the
  // same at any angle; the smaller 2026-10-16 necklaces need it for For Mom in the heart), then up to 10% smaller
  // (letters stay readable), then tumbling, then smaller. Second value: 0 tilts, 2 gems may tumble, 1 all may tumble
  var TRIES = [[1, 0], [1, 2], [0.95, 0], [0.9, 0], [1, 1], [0.95, 1], [0.9, 1], [0.85, 1], [0.8, 1], [0.75, 1], [0.7, 1],
               [0.65, 1], [0.6, 1], [0.55, 1], [0.5, 1]];
  var GEM = /^CH-FGL-BS-/;

  /** One gravity pass. Returns {pl: uid -> placement, ok: every charm placed without overlap, scale}. */
  function settle(base, items, fixed, opts) {
    opts = opts || {};
    var scale = opts.scale || 1, angles = opts.angles || TILTS, gemAngles = opts.gemTurn ? angles.concat(TUMBLE) : angles;
    var G = windowGrid(base);
    var occ = new Uint8Array(G.w * G.h);
    for (var i = 0; i < occ.length; i++) occ[i] = G.safe[i] ? 0 : 1;
    var out = {}, ok = true;
    function mark(pl) {
      var R = rotMask(pl.sku, pl.angle, pl.scale);
      for (var k = 0; k < R.cells.length; k += 2) {
        var x = pl.gx + R.cells[k], y = pl.gy + R.cells[k + 1];
        if (x >= 0 && y >= 0 && x < G.w && y < G.h) occ[y * G.w + x] = 2;
      }
    }
    function free(R, x0, y0) {
      for (var k = 0; k < R.cells.length; k += 2) {
        var x = x0 + R.cells[k], y = y0 + R.cells[k + 1];
        if (x < 0 || y < 0 || x >= G.w || y >= G.h || occ[y * G.w + x]) return false;
      }
      return true;
    }
    (fixed || []).forEach(function (pl) { out[pl.uid] = pl; mark(pl); });
    items.forEach(function (it, n) {
      if (out[it.uid] || !PV.charms[it.variant.sku]) return;
      var sku = it.variant.sku, best = null;
      (GEM.test(sku) ? gemAngles : angles).forEach(function (ang) {
        var R = rotMask(sku, ang, scale);
        var turn = Math.abs(ang) / 30;            // a 90-degree turn must land 3 rows (0.6 mm) lower to win
        for (var x0 = -1; x0 <= G.w - R.w + 1; x0++) {
          for (var y0 = G.h - R.h + 1; y0 >= -1; y0--) {
            if (!free(R, x0, y0)) continue;
            var yc = y0 + R.h / 2, xc = x0 + R.w / 2;
            var score = yc - 0.18 * Math.abs(xc - G.cx) - turn + hash(x0 * 31 + n * 7 + ang) * 0.6;
            if (!best || score > best.score) best = { score: score, gx: x0, gy: y0, angle: ang, R: R };
            break;                                  // the lowest free spot in this column is the one gravity finds
          }
        }
      });
      if (!best) { ok = false; return; }
      var pl = {
        uid: it.uid, sku: sku, angle: best.angle, scale: scale, gx: best.gx, gy: best.gy,
        x: G.x0 + (best.gx + best.R.w / 2) * CELL, y: G.y0 + (best.gy + best.R.h / 2) * CELL
      };
      out[it.uid] = pl;
      mark(pl);
    });
    return { pl: out, ok: ok, scale: scale };
  }

  var PACKS = {};   // fresh packs by locket + charm list (the set dialog re-renders on every pick)
  /** Every charm placed, none overlapping: keep `fixed` if the rest still fit around them, else a fresh pack. */
  function packAll(base, items, fixed) {
    items = items.filter(function (it) { return PV.charms[it.variant.sku]; });
    if (fixed && fixed.length) {
      var inc = settle(base, items, fixed, {});
      if (inc.ok) return inc;
    }
    var sig = base.key + '|' + items.map(function (it) { return it.variant.sku; }).join(',');
    var memo = PACKS[sig];
    if (!memo) {
      var orders = [sortBig(items), sortTall(items)];
      for (var ti = 0; ti < TRIES.length && !memo; ti++) {
        for (var oi = 0; oi < orders.length && !memo; oi++) {
          var r = settle(base, orders[oi], null, { scale: TRIES[ti][0], angles: TRIES[ti][1] === 1 ? TILTS.concat(TUMBLE) : TILTS,
                                                   gemTurn: TRIES[ti][1] === 2 });
          if (r.ok) memo = { order: orders[oi].map(function (it) { return items.indexOf(it); }), pl: orders[oi].map(function (it) { return r.pl[it.uid]; }), scale: r.scale };
        }
      }
      if (!memo) memo = { order: [], pl: [], scale: 0.5 };   // not reachable within capacity
      PACKS[sig] = memo;
    }
    var out = {};
    memo.order.forEach(function (idx, j) {
      var it = items[idx], p = memo.pl[j];
      out[it.uid] = { uid: it.uid, sku: p.sku, angle: p.angle, scale: p.scale, gx: p.gx, gy: p.gy, x: p.x, y: p.y };
    });
    return { pl: out, ok: true, scale: memo.scale };
  }

  var LAYOUT = {}; // per base: uid -> placement (kept so charms already in don't jump when one is added)
  function layoutFor(base, items, opts) {
    var key = (opts && opts.key) || 'main';
    var cache = LAYOUT[key];
    if (!cache || cache.base !== base.key || (opts && opts.fresh)) cache = { base: base.key, pl: {}, scale: 1 };
    var fixed = [];
    items.forEach(function (it) { if (cache.pl[it.uid] && cache.pl[it.uid].sku === it.variant.sku) fixed.push(cache.pl[it.uid]); });
    // a shrunk layout re-packs fresh, so it grows back to true size when charms come out
    var r = packAll(base, items, cache.scale === 1 ? fixed : null);
    cache.pl = r.pl; cache.scale = r.scale;
    LAYOUT[key] = cache;
    return r.pl;
  }
  function sortBig(items) {
    return items.slice().sort(function (a, b) {
      var A = PV.charms[a.variant.sku], B = PV.charms[b.variant.sku];
      return (B ? B.w_mm * B.h_mm : 0) - (A ? A.w_mm * A.h_mm : 0);
    });
  }
  function sortTall(items) {
    return items.slice().sort(function (a, b) {
      var A = PV.charms[a.variant.sku], B = PV.charms[b.variant.sku];
      return (B ? Math.max(B.w_mm, B.h_mm) : 0) - (A ? Math.max(A.w_mm, A.h_mm) : 0);
    });
  }
  /** Fresh pack (sets): the most compact pile, never overlapping. */
  function packFresh(base, items, key) {
    var r = packAll(base, items, null);
    LAYOUT[key] = { base: base.key, pl: r.pl, scale: r.scale };
    return r.pl;
  }

  function placeholderPhoto(ctx, cx, cy, r) {
    var g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.1, cx, cy, r);
    g.addColorStop(0, '#f3efe9'); g.addColorStop(1, '#d9d1c5');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath(); ctx.arc(cx, cy - r * 0.18, r * 0.3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(cx, cy + r * 0.62, r * 0.55, r * 0.42, 0, Math.PI, 0); ctx.fill();
  }

  var makeCanvas = K.makeCanvas;

  /** One charm drawn behind the glass onto ctx (canvas D px square). anim: {dy (mm), alpha} while dropping in. */
  function drawCharm(ctx, D, base, maps, it, pl, img, photoImg, anim) {
    var info = PV.charms[it.variant.sku];
    var g = base.geo, k = g.ppm * D, ks = k * (pl.scale || 1);
    var wpx = info.w_mm * ks, hpx = info.h_mm * ks;
    var t = -pl.angle * Math.PI / 180;
    var bw = Math.ceil(Math.abs(wpx * Math.cos(t)) + Math.abs(hpx * Math.sin(t))) + 4;
    var bh = Math.ceil(Math.abs(wpx * Math.sin(t)) + Math.abs(hpx * Math.cos(t))) + 4;
    var px = pl.x * k, py = (pl.y + (anim ? anim.dy : 0)) * k;
    var ox = Math.round(px - bw / 2), oy = Math.round(py - bh / 2);

    var c1 = makeCanvas(bw, bh), x1 = c1.getContext('2d');
    x1.translate(bw / 2, bh / 2); x1.rotate(t);
    if (info.photo) {
      var pcx = info.photo.cx_mm * ks - wpx / 2, pcy = info.photo.cy_mm * ks - hpx / 2, pr = info.photo.r_mm * ks;
      x1.save();
      x1.beginPath(); x1.arc(pcx, pcy, pr, 0, Math.PI * 2); x1.clip();
      if (photoImg) {
        var s = Math.max(2 * pr / photoImg.naturalWidth, 2 * pr / photoImg.naturalHeight);
        var iw = photoImg.naturalWidth * s, ih = photoImg.naturalHeight * s;
        x1.drawImage(photoImg, pcx - iw / 2, pcy - ih / 2, iw, ih);
      } else {
        placeholderPhoto(x1, pcx, pcy, pr);
      }
      x1.restore();
    }
    x1.drawImage(img, -wpx / 2, -hpx / 2, wpx, hpx);

    // behind the glass: shade (multiply), bright facet edges (screen), a faint veil of the glass
    var c2 = makeCanvas(bw, bh), x2 = c2.getContext('2d');
    x2.drawImage(c1, 0, 0);
    var N = maps.shade.naturalWidth, f = N / D;
    var sx = ox * f, sy = oy * f, sw = bw * f, sh = bh * f;
    x2.globalCompositeOperation = 'multiply';
    x2.drawImage(maps.shade, sx, sy, sw, sh, 0, 0, bw, bh);
    x2.globalCompositeOperation = 'destination-in';
    x2.drawImage(c1, 0, 0);
    x2.globalCompositeOperation = 'screen';
    x2.drawImage(maps.hi, sx, sy, sw, sh, 0, 0, bw, bh);
    x2.globalCompositeOperation = 'destination-in';
    x2.drawImage(c1, 0, 0);
    x2.globalCompositeOperation = 'source-atop';
    x2.globalAlpha = PV.glass.veil;
    x2.drawImage(maps.glass, sx, sy, sw, sh, 0, 0, bw, bh);

    ctx.save();
    windowPath(ctx, base, D);
    ctx.clip();
    var ct = PV.contact;
    ctx.shadowColor = 'rgba(0,0,0,' + ct.opacity + ')';
    ctx.shadowOffsetX = ct.dx * k; ctx.shadowOffsetY = ct.dy * k; ctx.shadowBlur = ct.blur * k * 2;
    ctx.globalAlpha = anim ? anim.alpha : 1;
    ctx.drawImage(c2, ox, oy);
    ctx.restore();
  }
  function windowPath(ctx, base, D) {
    var w = base.geo.window;
    ctx.beginPath();
    if (w.type === 'circle') {
      ctx.arc(w.cx * D, w.cy * D, w.r * D, 0, Math.PI * 2);
    } else {
      w.points.forEach(function (p, i) { if (i) ctx.lineTo(p[0] * D, p[1] * D); else ctx.moveTo(p[0] * D, p[1] * D); });
      ctx.closePath();
    }
  }

  function photoImgFor(it) {
    if (!it.photo) return Promise.resolve(null);
    var src = it.photo.local || it.photo.thumb || it.photo.url;
    return loadImg(src).catch(function () { return null; });
  }

  /**
   * Render a locket into canvases. job = {base, items, layoutKey, fresh, animateUid}. A newer render with the same
   * layoutKey supersedes an older one (its images may resolve later; it then draws nothing).
   */
  var SEQ = {};
  function render(canvases, job) {
    var key = job.layoutKey || 'main';
    var seq = SEQ[key] = (SEQ[key] || 0) + 1;
    canvases = canvases.filter(function (c) { return c && c.offsetParent !== null; });
    var base = job.base;
    if (!base || !canvases.length) return Promise.resolve();
    var items = job.items.filter(function (it) { return PV.charms[it.variant.sku]; });
    var pls = job.fresh ? packFresh(base, items, key) : layoutFor(base, items, { key: key });
    return Promise.all([
      baseMaps(base),
      Promise.all(items.map(function (it) { return loadImg(PV.charms[it.variant.sku].img).catch(function () { return null; }); })),
      Promise.all(items.map(photoImgFor))
    ]).then(function (r) {
      if (seq !== SEQ[key]) return;
      var maps = { img: r[0][0], shade: r[0][1], hi: r[0][2], glass: r[0][3] };
      var imgs = r[1], photos = r[2];
      function drawAll(ctx, D, skipUid) {
        ctx.clearRect(0, 0, D, D);
        ctx.drawImage(maps.img, 0, 0, D, D);
        items.forEach(function (it, i) {
          var pl = pls[it.uid];
          if (!pl || !imgs[i] || it.uid === skipUid) return;
          drawCharm(ctx, D, base, maps, it, pl, imgs[i], photos[i], null);
        });
      }
      var animIdx = -1;
      items.forEach(function (it, i) { if (it.uid === job.animateUid) animIdx = i; });
      if (animIdx < 0 || REDUCED || !pls[job.animateUid] || !imgs[animIdx]) {
        canvases.forEach(function (cv) { drawAll(cv.getContext('2d'), cv.width, null); });
        return;
      }
      // drop-in: everything else is drawn once into a cached layer; frames add only the falling charm
      var layers = canvases.map(function (cv) {
        var L = makeCanvas(cv.width, cv.width);
        drawAll(L.getContext('2d'), cv.width, job.animateUid);
        return L;
      });
      var it = items[animIdx], pl = pls[it.uid];
      return new Promise(function (done) {
        var t0 = null, dur = 520;
        function step(ts) {
          if (seq !== SEQ[key]) return done();   // a newer render owns these canvases now
          if (t0 === null) t0 = ts;
          var t = Math.min(1, (ts - t0) / dur);
          var e = 1 - Math.pow(1 - t, 3);
          canvases.forEach(function (cv, ci) {
            var ctx = cv.getContext('2d'), D = cv.width;
            ctx.clearRect(0, 0, D, D);
            ctx.drawImage(layers[ci], 0, 0);
            drawCharm(ctx, D, base, maps, it, pl, imgs[animIdx], photos[animIdx], t < 1 ? { dy: -(1 - e) * 9, alpha: Math.min(1, t * 2.2) } : null);
          });
          if (t < 1) requestAnimationFrame(step); else done();
        }
        requestAnimationFrame(step);
      });
    }).catch(function (e) { console.warn('[FloatingLocket] preview', e); });
  }

  var fitCanvas = K.fitCanvas;

  /* ------------------------------------------------------------------------------------------ the page */
  var els = {
    rail: $('[data-fl-sets-rail]'), railNav: $('[data-fl-rail-nav]'),
    bases: $('[data-fl-bases]'), sizes: $('[data-fl-sizes]'), sizesList: $('[data-fl-sizes-list]'),
    cats: $('[data-fl-cats]'), grid: $('[data-fl-grid]'),
    chips: $('[data-fl-chips]'), reviewBase: $('[data-fl-review-base]'), photoNote: $('[data-fl-photo-note]'),
    extras: $('[data-fl-extras]'), extrasWrap: $('[data-fl-extras-wrap]'),
    extrasHelp: $('[data-fl-extras-help]'), insideLabel: $('[data-fl-inside-label]'),
    atc: $('[data-fl-atc]'), error: $('[data-fl-error]'),
    stripBase: $('[data-fl-strip-base]'), stripCount: $('[data-fl-strip-count]'),
    review: $('[data-fl-canvas="review"]'), strip: $('[data-fl-canvas="strip"]'), zoom: $('[data-fl-canvas="zoom"]')
  };
  var dock = document.querySelector('[data-fl-dock]');
  var dockCanvas = dock ? dock.querySelector('[data-fl-canvas="dock"]') : null;
  var dockCaption = dock ? dock.querySelector('[data-fl-dock-caption]') : null;

  function mainCanvases() { return [els.review, els.strip, dockCanvas, dialogs.zoom && dialogs.zoom.open ? els.zoom : null]; }

  /* ---- Add to cart placement (owner 2026-10-14): on phones it sits under the locket line, above the charm list */
  K.placeActions($('[data-fl-actions]'), els.insideLabel);

  /* ---- quick start set cards */
  function itemsPrice(variants) { return variants.reduce(function (s, v) { return s + (v ? v.price : 0); }, 0); }
  function setVariants(set) { return set.items.map(function (i) { return SKU[i.sku] || null; }); }
  function setFits(set, base) { return fitsAll(base, setVariants(set)); }
  function setAvailable(set) { return setVariants(set).every(function (v) { return v && v.product.variants.some(function (x) { return x.available; }); }); }
  function cheapestBase(set) {
    var best = null;
    BASES.forEach(function (b) {
      if (!setFits(set, b)) return;
      b.product.variants.forEach(function (v) { if (v.available && (!best || v.price < best.price)) best = v; });
    });
    return best;
  }
  var SETS = (CFG.sets || []).filter(function (s) { return setAvailable(s) && cheapestBase(s); });
  /** a set's `featured` locket (its card image and the dialog's first choice) when it fits and is in stock */
  function featuredBase(set) {
    var b = set.featured && baseByKey(set.featured);
    return b && setFits(set, b) && inStock(b) && (!set.img || set.img[b.key]) ? b : null;
  }

  function renderSets() {
    if (!els.rail) return;
    var byo = $('[data-fl-byo-card]', els.rail);
    var html = SETS.map(function (s) {
      var bv = cheapestBase(s);
      var price = bv.price + itemsPrice(setVariants(s));
      var firstBase = featuredBase(s) || BASES.filter(function (b) {
        return setFits(s, b) && s.img && s.img[b.key] && b.product.variants.some(function (v) { return v.available; });
      })[0] || BASES.filter(function (b) { return s.img && s.img[b.key]; })[0];
      var img = firstBase ? s.img[firstBase.key] : '';
      return '<li class="fl-set">' +
        '<button type="button" class="fl-set__btn" data-fl-set="' + esc(s.id) + '" aria-haspopup="dialog">' +
          '<span class="fl-set__media"><img src="' + esc(img) + '" alt="" width="400" height="400" loading="lazy" decoding="async"></span>' +
          '<span class="fl-set__name">' + esc(s.name) + '</span>' +
          '<span class="fl-set__price">From ' + money(price) + '</span>' +
          '<span class="fl-set__cta" aria-hidden="true">Select</span>' +
        '</button></li>';
    }).join('');
    if (byo) byo.insertAdjacentHTML('beforebegin', html); else els.rail.insertAdjacentHTML('beforeend', html);
    els.rail.scrollLeft = 0; // scroll anchoring would keep the BYO card in view, i.e. scrolled to the end
    $$('[data-fl-set]', els.rail).forEach(function (b) {
      b.addEventListener('click', function () { openSet(b.getAttribute('data-fl-set'), b); });
    });
    var byoLink = $('[data-fl-byo-link]');
    if (byoLink) byoLink.addEventListener('click', function (e) { e.preventDefault(); goTo('FlStepBase'); });
    K.railNav(els.rail, els.railNav);
  }

  /* ---- step 1: lockets */
  function renderBases() {
    var html = BASES.map(function (b) {
      var avail = b.product.variants.some(function (v) { return v.available; });
      var id = 'FlBase-' + b.key;
      return '<label class="fl-base' + (avail ? '' : ' is-soldout') + '" for="' + id + '">' +
        '<input class="fl-base__input" type="radio" name="fl-base" id="' + id + '" value="' + esc(b.key) + '"' + (avail ? '' : ' disabled') + '>' +
        '<span class="fl-base__card">' +
          '<span class="fl-base__media"><img src="' + esc(b.geo.img) + '" alt="" width="300" height="300" loading="lazy" decoding="async"></span>' +
          '<span class="fl-base__name">' + esc(b.label) + '</span>' +
          '<span class="fl-base__meta">' + (avail ? 'Holds ' + b.min + '\u2013' + b.max + ' charms' : 'Sold out') + '</span>' +
          (CFG.showPrices ? '<span class="fl-base__price">' + money(minPrice(b)) + '</span>' : '') +
        '</span></label>';
    }).join('');
    els.bases.insertAdjacentHTML('beforeend', html);
    $$('.fl-base__input', els.bases).forEach(function (inp) {
      inp.addEventListener('change', function () { if (inp.checked) selectBase(inp.value, true); });
    });
  }
  function minPrice(b) { return Math.min.apply(null, b.product.variants.map(function (v) { return v.price; })); }

  function selectBase(key, user) {
    var b = baseByKey(key);
    if (!b) return;
    var changed = state.baseKey !== key;
    state.baseKey = key;
    if (changed) state.baseVariantId = null;
    var only = baseVariant(b);
    if (only) state.baseVariantId = only.id;
    $$('.fl-base__input', els.bases).forEach(function (inp) { inp.checked = inp.value === key; });
    renderSizes();
    if (user) {
      var ex = state.items.length - capacity();
      announce(b.label + ' selected. Holds ' + b.min + ' to ' + b.max + ' charms, depending on their size.' + (ex ? ' ' + plural(ex, 'charm is', 'charms are') + ' now extras.' : ''));
    }
    update({ relayout: changed });
  }

  function sizeOptionIndex(p) {
    for (var i = 0; i < p.options.length; i++) if (!/^(metal|color|colour|title)$/i.test(p.options[i])) return i;
    return -1;
  }
  function renderSizes() {
    var b = currentBase();
    var vs = b ? b.product.variants : [];
    if (!b || vs.length < 2) { els.sizes.hidden = true; els.sizesList.innerHTML = ''; return; }
    var oi = sizeOptionIndex(b.product);
    var legend = $('legend', els.sizes);
    if (legend) legend.textContent = oi > -1 ? (b.label.match(/ring/i) ? 'Ring size' : b.product.options[oi]) : 'Option';
    els.sizes.hidden = false;
    els.sizesList.innerHTML = vs.map(function (v) {
      var lab = oi > -1 ? v.options[oi] : v.title;
      var id = 'FlSize-' + v.id;
      return '<label class="fl-size' + (v.available ? '' : ' is-soldout') + '" for="' + id + '">' +
        '<input type="radio" name="fl-size" id="' + id + '" value="' + v.id + '"' + (v.available ? '' : ' disabled') +
        (state.baseVariantId === v.id ? ' checked' : '') + '>' +
        '<span>' + esc(lab) + (v.available ? '' : '<span class="visually-hidden"> sold out</span>') + '</span></label>';
    }).join('');
    $$('input', els.sizesList).forEach(function (inp) {
      inp.addEventListener('change', function () {
        state.baseVariantId = Number(inp.value);
        announce('Size ' + inp.parentNode.textContent.trim() + ' selected.');
        update({});
      });
    });
  }

  /* ---- step 2: charms */
  function renderCats() {
    var cats = (CFG.categories || [{ id: 'all', label: 'All' }]).filter(function (c) {
      return c.id === 'all' || CHARMS.some(function (p) { return inCat(p, c); });
    });
    els.cats.innerHTML = cats.map(function (c) {
      return '<button type="button" class="fl-chip-btn" data-fl-cat="' + esc(c.id) + '" aria-pressed="' + (c.id === state.cat) + '">' + esc(c.label) + '</button>';
    }).join('');
    $$('[data-fl-cat]', els.cats).forEach(function (b) {
      b.addEventListener('click', function () {
        state.cat = b.getAttribute('data-fl-cat');
        $$('[data-fl-cat]', els.cats).forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
        filterGrid();
        var n = $$('.fl-charm:not([hidden])', els.grid).length;
        announce(b.textContent + ': ' + plural(n, 'charm', 'charms') + '.');
      });
    });
  }
  function catById(id) { return (CFG.categories || []).filter(function (c) { return c.id === id; })[0]; }
  function inCat(p, c) {
    if (!c || c.id === 'all' || !c.match) return true;
    return c.match.some(function (m) { return p.handle.indexOf(m) > -1; });
  }
  function filterGrid() {
    var c = catById(state.cat);
    $$('.fl-charm', els.grid).forEach(function (li) {
      var p = CHARMS[Number(li.getAttribute('data-i'))];
      li.hidden = !inCat(p, c);
    });
  }

  function renderGrid() {
    els.grid.innerHTML = CHARMS.map(function (p, i) {
      var avail = p.variants.some(function (v) { return v.available; });
      var sub = p.isPhoto ? 'Upload your photo' : (p.multi ? rangeLabel(p) : '');
      var action = p.isPhoto ? 'Add' : (p.multi ? 'Choose' : 'Add');
      return '<li class="fl-charm' + (avail ? '' : ' is-soldout') + '" data-i="' + i + '">' +
        '<button type="button" class="fl-charm__btn" data-fl-charm="' + i + '"' + (p.multi || p.isPhoto ? ' aria-haspopup="dialog"' : '') + (avail ? '' : ' disabled') + '>' +
          '<span class="fl-charm__media"><img src="' + esc(p.img || '') + '" alt="" width="240" height="240" loading="lazy" decoding="async"></span>' +
          '<span class="fl-charm__name">' + esc(p.short) + '</span>' +
          (sub ? '<span class="fl-charm__sub">' + esc(sub) + '</span>' : '') +
          (avail ? '<span class="fl-charm__add" aria-hidden="true" data-fl-action="' + action + '">' + action + '</span>' : '<span class="fl-charm__sub">Sold out</span>') +
          '<span class="visually-hidden" data-fl-charm-state></span>' +
        '</button>' +
        '<span class="fl-charm__count" data-fl-count aria-hidden="true" hidden></span>' +
        (p.multi || p.isPhoto ? '' :
          '<button type="button" class="fl-charm__minus" data-fl-minus="' + i + '" hidden>' +
            '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14"><path d="M5 12h14" stroke="currentColor" stroke-width="2.4"/></svg>' +
            '<span class="visually-hidden">Remove one ' + esc(p.short) + '</span></button>') +
      '</li>';
    }).join('');
    $$('[data-fl-charm]', els.grid).forEach(function (b) {
      b.addEventListener('click', function () { onCharm(CHARMS[Number(b.getAttribute('data-fl-charm'))], b); });
    });
    $$('[data-fl-minus]', els.grid).forEach(function (b) {
      b.addEventListener('click', function () {
        var p = CHARMS[Number(b.getAttribute('data-fl-minus'))];
        for (var i = state.items.length - 1; i >= 0; i--) {
          if (state.items[i].variant.product === p) { removeItem(state.items[i].uid, b); break; }
        }
      });
    });
    filterGrid();
  }
  function rangeLabel(p) {
    var vals = p.variants.map(variantValue);
    if (/birthstone/i.test(p.pickOption)) return 'All 12 months';
    return vals[0] + '–' + vals[vals.length - 1];
  }

  /*
   * Extras (owner, 2026-10-14): one ordered list. The first `capacity` charms go INSIDE the locket; any after that are
   * EXTRA charms to swap in later, with no limit (owner 2026-10-14: no cap). EVERY charm ships loose (lockets are never pre-filled), so copy
   * never says only the extras ship loose: extra = more than the glass holds at once. "Swap in" / "Swap out" just
   * reorder the list, and removing a charm from inside lets the first extra move in.
   */
  function insideItems() { return state.items.slice(0, capacity()); }
  function extraItems() { return state.items.slice(capacity()); }
  /** full = not even the smallest charm would fit inside any more */
  function insideFull() {
    var b = currentBase(), k = capacity();
    if (!b) return false;
    if (state.items.length > k || k >= b.max) return true;
    if (k < b.min) return false;
    var used = 0;
    for (var i = 0; i < k; i++) used += sizeOf(state.items[i].variant);
    return used + smallestSize() > b.space;
  }
  function insideIds() { var o = {}; insideItems().forEach(function (it) { o[it.uid] = true; }); return o; }
  /** "Letter E is now inside your locket. Heart moved to extras." from the inside set before a change */
  function movesText(before) {
    var now = insideIds(), inn = [], out = [];
    state.items.forEach(function (it) {
      if (now[it.uid] && !before[it.uid]) inn.push(itemName(it.variant));
      if (!now[it.uid] && before[it.uid]) out.push(itemName(it.variant));
    });
    return (inn.length ? ' ' + inn.join(', ') + (inn.length > 1 ? ' are' : ' is') + ' now inside your locket.' : '') +
      (out.length ? ' ' + out.join(', ') + ' moved to extras.' : '');
  }

  function onCharm(p, btn) {
    clearError();
    if (!state.baseKey) {
      var first = BASES.filter(function (b) { return b.product.variants.some(function (v) { return v.available; }); })[0];
      if (first) selectBase(first.key, false);
    }
    if (p.isPhoto) { openPhoto({ onDone: function (photo) { addItem(p.variants.filter(function (v) { return v.available; })[0], photo); } }, btn); return; }
    if (p.multi) { openPicker(p, btn); return; }
    var v = p.variants.filter(function (x) { return x.available; })[0];
    if (v) addItem(v, null);
  }
  function addItem(v, photo) {
    var wasFull = insideFull();
    var it = newItem(v, photo);
    state.items.push(it);
    var extra = state.items.length > capacity();
    announce(!extra ? 'Added ' + itemName(v) + '. ' + countText() + '.'
      : wasFull ? 'Added ' + itemName(v) + ' as an extra charm. Your locket holds ' + capacity() + ' at a time, so you can swap it in anytime. ' + summaryText() + '.'
      : itemName(v) + ' is too big to fit inside with your other charms, so it was added as an extra charm you can swap in anytime. ' + summaryText() + '.');
    update({ animateUid: extra ? null : it.uid });
    return it;
  }
  /** Swap an extra in (it takes the last inside spot) or a charm out (to the end; extras that now fit move in). */
  function moveItem(id, toInside) {
    var k = capacity(), idx = -1, before = insideIds(), snapshot = state.items.slice();
    state.items.forEach(function (x, i) { if (x.uid === id) idx = i; });
    if (idx < 0) return;
    var it = state.items.splice(idx, 1)[0];
    if (toInside) {
      state.items.splice(Math.max(0, k - 1), 0, it);
      if (!insideIds()[it.uid]) {                       // too big for the spot it would take: undo
        state.items = snapshot;
        announce('There isn\u2019t room inside for ' + itemName(it.variant) + '. Remove or swap out another charm first.');
        var stay = $('[data-fl-swap="' + id + '"]', root);
        if (stay) stay.focus();
        return;
      }
    } else {
      state.items.push(it);
    }
    var msg = movesText(before);
    announce(msg.trim() || (itemName(it.variant) + ' moved.'));
    var nowIn = insideIds(), newIn = state.items.filter(function (x) { return nowIn[x.uid] && !before[x.uid]; })[0];
    update({ animateUid: newIn ? newIn.uid : null });
    var again = $('[data-fl-swap="' + id + '"]', root) || $('[data-fl-remove="' + id + '"]', root);
    if (again) again.focus();
  }
  function removeItem(id, focusFrom) {
    var idx = -1;
    state.items.forEach(function (it, i) { if (it.uid === id) idx = i; });
    if (idx < 0) return;
    var before = insideIds();
    var it = state.items.splice(idx, 1)[0];
    delete before[it.uid];
    var nowIn = insideIds(), movedIn = state.items.filter(function (x) { return nowIn[x.uid] && !before[x.uid]; })[0];
    announce('Removed ' + itemName(it.variant) + '.' + movesText(before) + ' ' + summaryText() + '.');
    update({ animateUid: movedIn ? movedIn.uid : null });
    if (!focusFrom) return;
    if (focusFrom.hasAttribute('data-fl-minus') && focusFrom.hidden) {
      // the grid's "-" hides itself at zero: keep focus on that charm's own button
      var card = focusFrom.closest('.fl-charm');
      var main = card && card.querySelector('[data-fl-charm]');
      if (main) main.focus();
    } else if (!document.body.contains(focusFrom)) {
      var next = $$('[data-fl-remove]', root)[Math.min(idx, state.items.length - 1)];
      (next || document.getElementById('FlStepCharms')).focus();
    }
  }
  function countText() { return plural(capacity(), 'charm', 'charms') + ' inside'; }
  function extrasText() { var e = Math.max(0, state.items.length - capacity()); return plural(e, 'extra charm', 'extra charms'); }
  /** "6 charms inside + 2 extra charms" */
  function summaryText() { return countText() + (state.items.length > capacity() ? ' + ' + extrasText() : ''); }

  /* ---- review + strip + dock */
  function chipHtml(it, extra, extrasExist) {
    var v = it.variant, name = itemName(v);
    var thumb = it.photo ? it.photo.thumb : (v.img || v.product.img);
    var swap = extra
      ? '<button type="button" class="fl-linkbtn fl-chip__swap" data-fl-swap="' + it.uid + '" data-fl-to="in">Swap in<span class="visually-hidden"> ' + esc(name) + '</span></button>'
      : (extrasExist ? '<button type="button" class="fl-linkbtn fl-chip__swap" data-fl-swap="' + it.uid + '" data-fl-to="out">Swap out<span class="visually-hidden"> ' + esc(name) + '</span></button>' : '');
    return '<li class="fl-chip' + (extra ? ' fl-chip--extra' : '') + '">' +
      '<span class="fl-chip__media' + (it.photo ? ' fl-chip__media--photo' : '') + '"><img src="' + esc(thumb || '') + '" alt="" width="40" height="40" loading="lazy"></span>' +
      '<span class="fl-chip__name">' + esc(name) + '</span>' +
      (v.product.isPhoto ? '<button type="button" class="fl-linkbtn fl-chip__edit" data-fl-photo-edit="' + it.uid + '">Change photo<span class="visually-hidden"> for ' + esc(name) + '</span></button>' : '') +
      swap +
      '<button type="button" class="fl-iconbtn fl-chip__x" data-fl-remove="' + it.uid + '">' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2"/></svg>' +
        '<span class="visually-hidden">Remove ' + esc(name) + '</span></button>' +
    '</li>';
  }
  function renderChips() {
    // keep focus on the same chip control across the re-render (Change photo, resize, ...)
    var ae = document.activeElement, keep = null;
    var lists = [els.chips, els.extras].filter(Boolean);
    if (ae && lists.some(function (l) { return l.contains(ae); })) {
      ['data-fl-remove', 'data-fl-photo-edit', 'data-fl-swap'].forEach(function (k) {
        if (!keep && ae.hasAttribute(k)) keep = '[' + k + '="' + ae.getAttribute(k) + '"]';
      });
    }
    var inside = insideItems(), extras = extraItems();
    els.chips.innerHTML = inside.map(function (it) { return chipHtml(it, false, extras.length > 0); }).join('');
    if (els.extras) {
      els.extras.innerHTML = extras.map(function (it) { return chipHtml(it, true, true); }).join('');
      els.extrasWrap.hidden = !extras.length;
    }
    if (els.insideLabel) els.insideLabel.hidden = !extras.length;
    $$('[data-fl-remove]', root).forEach(function (b) {
      b.addEventListener('click', function () { removeItem(b.getAttribute('data-fl-remove'), b); });
    });
    $$('[data-fl-swap]', root).forEach(function (b) {
      b.addEventListener('click', function () { moveItem(b.getAttribute('data-fl-swap'), b.getAttribute('data-fl-to') === 'in'); });
    });
    $$('[data-fl-photo-edit]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        var id = b.getAttribute('data-fl-photo-edit');
        openPhoto({ onDone: function (photo) {
          state.items.forEach(function (it) { if (it.uid === id) it.photo = photo; });
          announce('Photo updated.');
          update({});
          var again = $('[data-fl-photo-edit="' + id + '"]', root);
          if (again) again.focus();
        } }, b);
      });
    });
    if (keep) { var k = $(keep, root); if (k) k.focus(); }
  }

  /** what the picture shows (inside only); the extras row under it names the extras */
  function previewLabel(base, items) {
    if (!base) return 'Choose a locket to see your preview';
    if (!items.length) return 'Preview: an empty ' + base.label + ' locket';
    return 'Preview: ' + base.label + ' locket with ' + items.map(function (it) { return itemName(it.variant); }).join(', ');
  }

  /* ---- "Extra charms" row under the review preview and the desktop dock (owner 2026-10-14): the glass shows what
     fits at once, the row shows the rest, so nothing the shopper added is missing from the picture */
  var trays = [];
  (function makeTrays() {
    function tray() {
      var t = document.createElement('div');
      t.className = 'fl-tray';
      t.hidden = true;
      t.innerHTML = '<span class="fl-tray__thumbs" aria-hidden="true"></span>' +
        '<span class="fl-tray__text"><span class="fl-tray__count"></span><span class="fl-tray__sub"></span></span>';
      return t;
    }
    var pv = $('.fl-review__preview');
    if (pv) { var a = tray(); pv.parentNode.insertBefore(a, pv.nextSibling); trays.push(a); }
    if (dockCanvas) { var d = tray(); dockCanvas.parentNode.insertBefore(d, dockCanvas.nextSibling); trays.push(d); }
  })();
  function trayThumb(it) {
    if (it.photo) return it.photo.local || it.photo.thumb;
    var c = PV.charms[it.variant.sku];
    return c && c.img ? c.img : (it.variant.img || it.variant.product.img);   // the charm itself, trimmed
  }
  function renderTrays(extras) {
    var n = extras.length;
    var sig = extras.map(function (it) { return it.uid + (it.photo ? it.photo.url : ''); }).join('|');
    var thumbs = extras.slice(0, 6).map(function (it) {   // 6 fit one line on any phone; the count says the rest
      return '<img src="' + esc(trayThumb(it)) + '" alt="" width="28" height="28"' + (it.photo ? ' class="is-photo"' : '') + '>';
    }).join('');
    var names = extras.map(function (it) { return itemName(it.variant); }).join(', ');
    trays.forEach(function (t) {
      t.hidden = !n;
      if (!n || t.__sig === sig) return;          // unchanged: don't reload the thumbnails
      t.__sig = sig;
      $('.fl-tray__thumbs', t).innerHTML = thumbs;
      $('.fl-tray__count', t).innerHTML = '+' + esc(plural(n, 'extra charm', 'extra charms')) + '<span class="visually-hidden">: ' + esc(names) + '. </span>';
      $('.fl-tray__sub', t).textContent = n === 1 ? 'Swap it in anytime' : 'Swap them in anytime';
    });
  }

  function update(opts) {
    opts = opts || {};
    var b = currentBase();
    var n = state.items.length, cap = capacity();
    // no capacity bar, "locket is full" note or "Add extra" pill in step 2 (owner 2026-10-14): inside vs extra shows
    // in the strip, the review and the status announcements
    var nExtra = n - cap, inFull = insideFull();
    // grid card states
    $$('.fl-charm', els.grid).forEach(function (li) {
      var p = CHARMS[Number(li.getAttribute('data-i'))];
      var c = state.items.filter(function (it) { return it.variant.product === p; }).length;
      var badge = $('[data-fl-count]', li), minus = $('[data-fl-minus]', li), st = $('[data-fl-charm-state]', li);
      badge.hidden = !c; badge.textContent = c ? '\u00d7' + c : '';
      if (minus) minus.hidden = !c;
      li.classList.toggle('is-in', !!c);
      if (st) st.textContent = (c ? ', ' + c + ' added' : '') + (inFull ? ', adds as an extra charm' : '');
    });
    renderChips();
    // review text
    var bv = baseVariant(b, state.baseVariantId);
    var sizeIdx = b ? sizeOptionIndex(b.product) : -1;
    els.reviewBase.innerHTML = b
      ? '<strong>' + esc(b.label) + '</strong>' + (bv && b.product.variants.length > 1 && sizeIdx > -1 ? ' &middot; Size ' + esc(bv.options[sizeIdx]) : '') +
        (b.blurb ? '<br><span class="fl-help">' + esc(b.blurb) + '</span>' : '')   // chain length only, no glass size (owner 2026-10-14)
      : 'Choose a locket in step 1.';
    els.photoNote.hidden = !state.items.some(function (it) { return it.variant.product.isPhoto; });
    if (els.extrasHelp) els.extrasHelp.textContent = nExtra ? 'Your locket holds ' + cap + ' at a time. Swap ' + (nExtra === 1 ? 'this one' : 'these') + ' in whenever you like.' : '';
    if (CFG.showPrices) {
      var total = (bv ? bv.price : 0) + itemsPrice(state.items.map(function (it) { return it.variant; }));
      $('[data-fl-total-note]').textContent = 'Total ' + money(total);
    }
    // strip + dock text
    els.stripBase.textContent = b ? b.label : 'Choose a locket';
    var byoImg = $('[data-fl-byo-card] img');
    if (byoImg && b && byoImg.getAttribute('src') !== b.geo.img) byoImg.setAttribute('src', b.geo.img);
    els.stripCount.textContent = n ? summaryText() : 'No charms yet';
    if (dockCaption) dockCaption.textContent = b ? b.label + (n ? ' \u00b7 ' + summaryText() : ' \u00b7 add charms in step 2') : 'Choose a locket to start';
    // preview: only what's inside; the extras row under it shows the rest
    var label = previewLabel(b, insideItems());
    [els.review, els.zoom, dockCanvas].forEach(function (cv) { if (cv) cv.setAttribute('aria-label', label); });
    renderTrays(b ? extraItems() : []);
    var rv = $('.fl-review');
    if (rv) rv.classList.toggle('has-extras', !!(b && nExtra > 0));   // phones: the preview shrinks a bit to make room
    if (b) {
      mainCanvases().forEach(fitCanvas);
      render(mainCanvases(), { base: b, items: insideItems(), layoutKey: 'main', animateUid: opts.animateUid });
    }
    clearError();
    updateAtc();
    save();
  }

  /* ---- errors */
  function showError(msg) { if (els.error) els.error.textContent = msg; }
  function clearError() { if (els.error) els.error.textContent = ''; }

  /* ---------------------------------------------------------------------------------------------- dialogs */
  /* ---- variant picker (letters, numbers, birthstones): the kit */
  var PICK = K.picker(dialogs.picker, UI);
  /** The set dialog's birth month: a grid of the 12 stones (the picker dialog, one choice, then it closes). */
  function openStonePicker(i, opener) {
    var it = setState.items[i], p = it.variant.product;
    PICK.openOne(p, opener, {
      current: it.variant, thumb: stoneThumb,
      onPick: function (v) {
        it.variant = v;
        renderSetBody();
        var f = $('[data-fl-set-stone="' + i + '"]', dialogs.set);
        if (f) f.focus();
        announce((it.cfg.label || 'Birth month') + ': ' + variantLabel(v) + '.');
      }
    });
  }

  function openPicker(p, opener) {
    PICK.open(p, opener, {
      thumb: stoneThumb,
      help: function () {
        return insideFull()
          ? 'Your locket holds ' + capacity() + ' at a time. New picks are added as extra charms to swap in anytime. ' + summaryText() + '.'
          : 'Tap to add. ' + countText() + '.';
      },
      count: function (v) { return state.items.filter(function (it) { return it.variant === v; }).length; },
      add: function (v) { addItem(v, null); },
      removeOne: function (v) {
        for (var i = state.items.length - 1; i >= 0; i--) if (state.items[i].variant === v) { removeItem(state.items[i].uid); break; }
      }
    });
  }

  /* ---- photo: choose -> crop (Pintura when loaded) -> upload to Cloudinary (the kit) */
  var PHOTO = K.photoDialog(dialogs.photo, UI, { cloudinary: CFG.cloudinary, log: '[FloatingLocket]' });
  function openPhoto(ctx, opener) { PHOTO.open(ctx, opener); }

  /* ---- zoom (phones: tap the strip's mini preview) */
  var zoomBtn = $('[data-fl-strip-zoom]');
  if (zoomBtn) zoomBtn.addEventListener('click', function () {
    if (!currentBase()) { goTo('FlStepBase'); return; }
    openDialog(dialogs.zoom, zoomBtn);
    setTimeout(function () { fitCanvas(els.zoom); render([els.zoom], { base: currentBase(), items: insideItems(), layoutKey: 'main' }); }, 0);
  });

  /* ---- quick-start set dialog */
  var setState = null;
  function openSet(id, opener) {
    var set = SETS.filter(function (s) { return s.id === id; })[0];
    if (!set) return;
    var fits = BASES.filter(function (b) { return setFits(set, b) && b.product.variants.some(function (v) { return v.available; }); });
    var fb = featuredBase(set);
    if (fb) fits = [fb].concat(fits.filter(function (b) { return b !== fb; }));
    setState = {
      set: set,
      baseKey: fits[0].key,
      baseVariantId: null,
      items: set.items.map(function (si) { return { uid: uid(), variant: inStockVariant(SKU[si.sku]), photo: null, cfg: si }; })
    };
    var only = baseVariant(fits[0]);
    if (only) setState.baseVariantId = only.id;
    var d = dialogs.set;
    $('[data-fl-set-title]', d).textContent = set.name;
    renderSetBody();
    $('[data-fl-set-error]', d).textContent = '';
    openDialog(d, opener);
    track('floating_set_view', { set_id: set.id });
  }

  function renderSetBody() {
    var d = dialogs.set, s = setState, set = s.set;
    var body = $('[data-fl-set-body]', d);
    // only the lockets this set fits (owner 10/15, as in the mini builder: no greyed "Won't fit" card); a sold-out
    // locket still shows, greyed
    var shown = BASES.filter(function (b) { return setFits(set, b); });
    var basesHtml = shown.map(function (b) {
      var avail = b.product.variants.some(function (v) { return v.available; });
      var id = 'FlSetBase-' + b.key;
      var why = !avail ? 'Sold out' : 'Holds ' + b.min + '\u2013' + b.max;
      return '<label class="fl-base fl-base--sm' + (avail ? '' : ' is-soldout') + '" for="' + id + '">' +
        '<input class="fl-base__input" type="radio" name="fl-set-base" id="' + id + '" value="' + b.key + '"' +
          (s.baseKey === b.key ? ' checked' : '') + (avail ? '' : ' disabled') + '>' +
        '<span class="fl-base__card"><span class="fl-base__media"><img src="' + esc(b.geo.img) + '" alt="" width="160" height="160" loading="lazy"></span>' +
        '<span class="fl-base__text"><span class="fl-base__name">' + esc(b.short) + '</span><span class="fl-base__meta">' + esc(why) + '</span></span></span></label>';
    }).join('');
    var b = baseByKey(s.baseKey);
    var sizes = '';
    if (b && b.product.variants.length > 1) {
      var oi = sizeOptionIndex(b.product);
      sizes = '<fieldset class="fl-sizes"><legend class="fl-label">Ring size</legend><div class="fl-sizes__list">' +
        b.product.variants.map(function (v) {
          var vid = 'FlSetSize-' + v.id;
          return '<label class="fl-size' + (v.available ? '' : ' is-soldout') + '" for="' + vid + '"><input type="radio" name="fl-set-size" id="' + vid + '" value="' + v.id + '"' +
            (s.baseVariantId === v.id ? ' checked' : '') + (v.available ? '' : ' disabled') + '><span>' + esc(oi > -1 ? v.options[oi] : v.title) + '</span></label>';
        }).join('') + '</div></fieldset>';
    }
    var picks = s.items.map(function (it, i) {
      var p = it.variant.product;
      if (p.isPhoto) {
        return '<div class="fl-field fl-field--photo"><span class="fl-label" id="FlSetPhotoLabel' + i + '">' + (it.cfg.label || 'Photo') + '</span>' +
          K.uploadButton({ hook: 'data-fl-set-photo="' + i + '"', labelId: 'FlSetPhotoLabel' + i, photo: it.photo ? (it.photo.local || it.photo.thumb) : null }) + '</div>';
      }
      if (!it.cfg.pick || !p.multi) return '';
      var sid = 'FlSetPick-' + i;
      if (isStone(p)) {
        return '<div class="fl-field"><span class="fl-label" id="' + sid + '-l">' + esc(it.cfg.label || CFG.pickLabels[p.pickOption] || p.pickOption) + '</span>' +
          '<button type="button" class="fl-select fl-select--stone" data-fl-set-stone="' + i + '" aria-haspopup="dialog" aria-labelledby="' + sid + '-l ' + sid + '-v">' +
            '<img class="fl-select__thumb" src="' + esc(stoneThumb(it.variant)) + '" alt="" width="28" height="28">' +
            '<span class="fl-select__text" id="' + sid + '-v">' + esc(variantLabel(it.variant)) + '</span></button></div>';
      }
      return '<div class="fl-field"><label class="fl-label" for="' + sid + '">' + esc(it.cfg.label || CFG.pickLabels[p.pickOption] || p.pickOption) + '</label>' +
        '<select class="fl-select" id="' + sid + '" data-fl-set-pick="' + i + '">' +
        p.variants.map(function (v) {
          return '<option value="' + v.id + '"' + (v === it.variant ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + esc(variantLabel(v)) + (v.available ? '' : ' (sold out)') + '</option>';
        }).join('') + '</select></div>';
    }).join('');
    var inside = s.items.map(function (it) {
      var v = it.variant;
      return '<li><img src="' + esc(it.photo ? (it.photo.local || it.photo.thumb) : (v.img || v.product.img)) + '" alt="" width="32" height="32" loading="lazy"' + (it.photo ? ' class="is-photo"' : '') + '>' + esc(itemName(v)) + '</li>';
    }).join('');
    body.innerHTML =
      '<div class="fl-setdlg">' +
        '<div class="fl-setdlg__preview"><canvas class="fl-canvas" data-fl-canvas="set" width="340" height="340" role="img" aria-label="' + esc(previewLabel(b, s.items)) + '"></canvas>' +
          '<p class="fl-setdlg__tag">' + esc(set.tagline || '') + '</p></div>' +
        '<div class="fl-setdlg__form">' +
          '<fieldset class="fl-bases fl-bases--sm fl-bases--n' + shown.length + '"><legend class="fl-label">Locket</legend>' + basesHtml + '</fieldset>' +
          sizes +
          (picks ? '<div class="fl-setdlg__picks">' + picks + '</div>' : '') +
          '<div class="fl-field fl-field--inside"><span class="fl-label">Inside your locket</span><ul class="fl-inside" role="list">' + inside + '</ul></div>' +
        '</div>' +
      '</div>';
    $$('input[name="fl-set-base"]', body).forEach(function (inp) {
      inp.addEventListener('change', function () {
        s.baseKey = inp.value;
        var bb = baseByKey(inp.value), only = baseVariant(bb);
        s.baseVariantId = only ? only.id : null;
        renderSetBody();
        var again = $('#FlSetBase-' + inp.value, body);
        if (again) again.focus();
      });
    });
    $$('input[name="fl-set-size"]', body).forEach(function (inp) {
      inp.addEventListener('change', function () { s.baseVariantId = Number(inp.value); updateSetFoot(); });
    });
    $$('[data-fl-set-pick]', body).forEach(function (sel) {
      sel.addEventListener('change', function () {
        var it = s.items[Number(sel.getAttribute('data-fl-set-pick'))];
        var v = it.variant.product.variants.filter(function (x) { return x.id === Number(sel.value); })[0];
        if (v) { it.variant = v; drawSetPreview(); refreshInside(); updateSetFoot(); }
      });
    });
    if ($('[data-fl-set-stone]', body)) {
      CHARMS.forEach(function (cp) { if (isStone(cp)) cp.variants.forEach(function (v) { loadImg(stoneThumb(v)).catch(function () {}); }); });
    }
    $$('[data-fl-set-stone]', body).forEach(function (btn) {
      btn.addEventListener('click', function () { openStonePicker(Number(btn.getAttribute('data-fl-set-stone')), btn); });
    });
    $$('[data-fl-set-photo]', body).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = Number(btn.getAttribute('data-fl-set-photo'));
        // the photo dialog stacks on top of the set dialog (both modal); on close focus returns to this button
        openPhoto({ onDone: function (photo) {
          s.items[i].photo = photo;
          renderSetBody();
          var again = $('[data-fl-set-photo="' + i + '"]', body);
          if (again) again.focus();
          announce('Photo added to your ' + set.name + ' set.');
        } }, btn);
      });
    });
    updateSetFoot();
    setTimeout(drawSetPreview, 0);   // after layout (a hidden tab never runs rAF, which left the preview blank)
  }
  function refreshInside() {
    var body = $('[data-fl-set-body]', dialogs.set);
    var ul = $('.fl-inside', body);
    if (!ul) return;
    ul.innerHTML = setState.items.map(function (it) {
      var v = it.variant;
      return '<li><img src="' + esc(it.photo ? (it.photo.local || it.photo.thumb) : (v.img || v.product.img)) + '" alt="" width="32" height="32" loading="lazy"' + (it.photo ? ' class="is-photo"' : '') + '>' + esc(itemName(v)) + '</li>';
    }).join('');
  }
  function drawSetPreview() {
    var cv = $('[data-fl-canvas="set"]', dialogs.set);
    if (!cv || !setState) return;
    var b = baseByKey(setState.baseKey);
    fitCanvas(cv);
    cv.setAttribute('aria-label', previewLabel(b, setState.items));
    render([cv], { base: b, items: setState.items, layoutKey: 'set', fresh: true });
  }
  function setMissing() {
    var s = setState;
    var b = baseByKey(s.baseKey);
    if (!baseVariant(b, s.baseVariantId)) return 'Choose a ring size';
    if (s.items.some(function (it) { return it.variant.product.isPhoto && !it.photo; })) return 'Upload your photo to continue';
    if (s.items.some(function (it) { return !it.variant.available; })) return 'One of these charms is sold out';
    return '';
  }
  function updateSetFoot() {
    var d = dialogs.set, s = setState;
    var btn = $('[data-fl-set-atc]', d);
    var b = baseByKey(s.baseKey), bv = baseVariant(b, s.baseVariantId);
    var price = (bv ? bv.price : minPrice(b)) + itemsPrice(s.items.map(function (it) { return it.variant; }));
    var miss = setMissing();
    btn.textContent = miss ? miss + ' — ' + money(price) : 'Add set to cart — ' + money(price);
    btn.setAttribute('aria-disabled', miss ? 'true' : 'false');
    btn.classList.toggle('is-disabled', !!miss);
  }
  (function wireSet() {
    var d = dialogs.set;
    if (!d) return;
    $('[data-fl-set-atc]', d).addEventListener('click', function () {
      var s = setState, err = $('[data-fl-set-error]', d);
      var miss = setMissing();
      if (miss) {
        err.textContent = miss + '.';
        var target = miss.indexOf('photo') > -1 ? $('[data-fl-set-photo]', d) : $('input[name="fl-set-size"]:not(:disabled)', d);
        if (target) target.focus();
        return;
      }
      err.textContent = '';
      var b = baseByKey(s.baseKey);
      var btn = this;
      addToCart(baseVariant(b, s.baseVariantId), s.items, s.set.id, btn).then(function (r) {
        if (r.ok) closeDialog(d); else if (!r.busy) err.textContent = r.msg || 'Something went wrong adding your set. Please try again.';
      });
    });
    $('[data-fl-set-customize]', d).addEventListener('click', function () {
      var s = setState;
      state.baseKey = s.baseKey;
      state.baseVariantId = s.baseVariantId;
      state.items = s.items.map(function (it) { return newItem(it.variant, it.photo); });
      LAYOUT.main = null;
      $$('.fl-base__input', els.bases).forEach(function (inp) { inp.checked = inp.value === state.baseKey; });
      renderSizes();
      d.__onClose = function () {
        update({});
        goTo('FlStepCharms');
        announce(s.set.name + ' set loaded into your locket. Add or remove charms below.');
      };
      closeDialog(d);
    });
  })();

  /* ---------------------------------------------------------------------------------------------- cart */
  var track = K.track;

  /** Resolves {ok, msg}. One request at a time (the kit): a double click or a held Enter must not add two lockets. */
  function addToCart(bv, items, setId, btn, insideCount) {
    if (!bv) return Promise.resolve({ ok: false, msg: '' });
    var bundleId = 'floating_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    var parent = {
      _charm_bundle_id: bundleId,
      _charm_bundle_parent: 'true',
      _charm_bundle_charm_count: String(items.length),
      _floating_locket: 'true',
      _floating_set: setId || 'custom'
    };
    var k = insideCount == null ? items.length : Math.min(insideCount, items.length);
    parent._floating_inside_count = String(k);
    parent._floating_extra_count = String(items.length - k);
    // visible: Charm 1..N for every charm, like Build Your Charm Set (owner 2026-10-14: no "Extra" in the cart; every
    // charm ships loose). Which ones fit inside stays in the hidden _floating_inside_count / _floating_extra.
    items.forEach(function (it, i) { parent['Charm ' + (i + 1)] = cartTitle(it.variant); });
    var lines = [{ id: bv.id, quantity: 1, properties: parent }];
    items.forEach(function (it, i) {
      var props = {
        _charm_bundle_id: bundleId,
        _charm_bundle_child: 'true',
        _slot_position: String(i + 1),
        _charm_title: it.variant.product.title
      };
      if (i >= k) props._floating_extra = 'true';
      if (it.photo) { props.photo = it.photo.url; props._circlerender = 'CIRCLE'; }
      lines.push({ id: it.variant.id, quantity: 1, properties: props });
    });
    return K.addLines(lines, {
      btn: btn, log: '[FloatingLocket]',
      soldOut: 'Sorry, part of your locket just sold out. Please refresh and try again.',
      failed: 'Something went wrong adding your locket. Please try again.',
      onRestore: function () { if (btn === els.atc) updateAtc(); else if (setState) updateSetFoot(); },
      onAdded: function () {
        announce('Added to your cart.');
        track('floating_locket_add', { set_id: setId || 'custom', base_sku: bv.sku, charms: k, extras: items.length - k });
        try {
          if (window._learnq) {
            var host = window.item || {};
            window._learnq.push(['track', 'Added to Cart', {
              Name: bv.product.title + (setId && setId !== 'custom' ? ' (' + setId + ' set)' : ' (floating locket)'),
              ProductID: bv.product.id, VariantID: bv.id, SKU: bv.sku,
              Price: (bv.price + itemsPrice(items.map(function (it) { return it.variant; }))) / 100,
              Charms: items.slice(0, k).map(function (it) { return cartTitle(it.variant); }),
              Extras: items.slice(k).map(function (it) { return cartTitle(it.variant); }),
              URL: host.URL || window.location.href, ImageURL: host.ImageURL || '', Brand: host.Brand || 'Wear Felicity'
            }]);
          }
        } catch (e) { /* ok */ }
      }
    });
  }

  function atcBlocker() {
    var b = currentBase();
    if (!b) return { msg: 'Choose your locket in step 1', go: 'FlStepBase' };
    if (!inStock(b)) return { msg: 'The ' + b.label + ' is sold out. Choose another locket', go: 'FlStepBase' };
    if (!baseVariant(b, state.baseVariantId)) return { msg: 'Choose a ring size', go: 'FlStepBase' };
    if (!state.items.length) return { msg: 'Add at least one charm', go: 'FlStepCharms' };
    if (state.items.some(function (it) { return it.variant.product.isPhoto && !it.photo; })) return { msg: 'Add a photo to your photo charm', go: 'FlStepReview' };
    var gone = state.items.filter(function (it) { return !it.variant.available; })[0];
    if (gone) return { msg: itemName(gone.variant) + ' is sold out. Remove it to continue', go: 'FlStepReview' };
    return null;
  }
  function updateAtc() {
    var bl = atcBlocker();
    els.atc.setAttribute('aria-disabled', bl ? 'true' : 'false');
    els.atc.classList.toggle('is-disabled', !!bl);
    els.atc.textContent = bl ? bl.msg : 'Add to cart';
  }
  els.atc.addEventListener('click', function () {
    var bl = atcBlocker();
    if (bl) { showError(bl.msg + '.'); goTo(bl.go); return; }
    clearError();
    addToCart(baseVariant(currentBase(), state.baseVariantId), state.items, 'custom', els.atc, capacity()).then(function (r) {
      if (!r.ok && !r.busy) showError(r.msg || 'Something went wrong adding your locket. Please try again.');
    });
  });

  /* ---------------------------------------------------------------------------------------- navigation */
  var goTo = K.goTo;
  [$('[data-fl-review-link]'), document.querySelector('[data-fl-dock-review]')].forEach(function (a) {
    if (a) a.addEventListener('click', function (e) { e.preventDefault(); goTo('FlStepReview'); });
  });

  /** Sticky offset: the theme's sticky header height, so the strip/dock sit just under it (the kit). */
  var syncOffset = K.stickyOffset();
  var lastW = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastW) return;   // phones fire resize when the URL bar slides; only a real width change re-renders
    lastW = window.innerWidth;
    update({});
  });

  /* -------------------------------------------------------------------------------------------- init */
  function init() {
    document.documentElement.classList.add('fl-page');
    if (dock) { dock.hidden = false; root.classList.add('fl--docked'); document.documentElement.classList.add('fl-has-dock'); }
    renderSets();
    renderBases();
    renderCats();
    renderGrid();
    var restored = restore();
    if (!state.baseKey) {
      var first = BASES.filter(function (b) { return b.product.variants.some(function (v) { return v.available; }); })[0];
      if (first) state.baseKey = first.key;
      var only = first ? baseVariant(first) : null;
      if (only) state.baseVariantId = only.id;
    }
    $$('.fl-base__input', els.bases).forEach(function (inp) { inp.checked = inp.value === state.baseKey; });
    renderSizes();
    syncOffset();
    update({});
    // ?set=<id> opens that set (ads can link straight to a set)
    var m = /[?&]set=([a-z0-9-]+)/i.exec(window.location.search);
    if (m && SETS.some(function (s) { return s.id === m[1]; })) {
      var card = $('[data-fl-set="' + m[1] + '"]');
      openSet(m[1], card);
    }
    if (restored && state.items.length) announce('Your locket was restored: ' + countText() + '.');
    // warm the other lockets' maps when idle
    var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1500); };
    idle(function () { BASES.forEach(function (b) { baseMaps(b).catch(function () {}); }); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
